// Portage TypeScript du moteur d'analyse de seance de feuille/Code.gs
// (fonctions ANALYSE_SEANCE, RPE_SEANCE_MULTIBLOCS, UA_RPE_SEANCE et
// leurs dependances). La logique est reprise a l'identique - seuls les
// noms de types et l'organisation en modules changent - pour que le
// calcul de duree/distance/UA/RPE reste coherent avec le classeur
// Google Sheets utilise par ailleurs pour construire les seances.
//
// Grammaire acceptee (voir feuille/Code.gs pour les exemples complets) :
//   - un bloc simple      : "4x(8'@MP-R3')", "6x(1000m@10K-R2')"
//   - une composition "+" : "6x(600m@5K-R1') + 2x(1200m@HP-R2')"
//   - un bloc COMPOSE     : "3x(8'@MP-R1'30''-3'@10k-R3')" (plusieurs
//     efforts par cycle, un cycle repete n fois)
//   - un bloc IMBRIQUE    : "2x(5x(1'15''@5k-R1'15'')-R3')" (un sous-bloc
//     Nx(...) deja forme, repete n fois avec une recup entre repetitions)
//   - un effort PROGRESSIF : "30'@HP-10K" (30' en progressant de l'allure
//     semi a l'allure 10 km), deux codes d'allure separes par "-", calcule
//     a la moyenne des deux allures (voir lireAllure)
//   - une CONDITION d'execution : "10x(1'@5k[côte]-R1')", une etiquette
//     entre crochets juste apres l'allure (côte, descente, piste, trail,
//     tapis, cf CONDITIONS). Elle ne change pas les calculs (retiree avant
//     l'analyse) ; elle est affichee et envoyee vers intervals.icu.
//
// Important : un prefixe "Nx" doit toujours etre suivi de parentheses
// explicites autour du contenu repete ("6x(1000m@10K-R2')", jamais
// "6x1000m@10K-R2'" sans parentheses) - cette forme sans parentheses
// etait auparavant toleree silencieusement comme equivalente, mais c'est
// une source d'ambiguite/d'erreurs de saisie non detectees : elle est
// desormais rejetee (voir analyserBlocPourRPE). De meme, contrairement a
// la syntaxe "AxBx(...)" (sans parenthese interne) evoquee au depart,
// seule la forme AVEC parenthese autour du sous-bloc est reconnue pour
// l'imbrication : "2x(3x(...))", pas "2x3x(...)". Les distances d'effort
// s'ecrivent en "km", "k" ou "m" (ex: "1km", "10k@EASY", "5,5k", "1000m") :
// avant le "@", "k"/"K" est toujours une distance en kilometres ; apres le
// "@", "10K" reste le code d'allure 10 km (voir parseValeurEffort). Les
// recuperations n'acceptent pas "k" ("R1km", pas "R1k") : "10K" y serait
// sinon lu comme 10 km au lieu d'un code d'allure (cf estCodeAllure).

import { ALLURE_TABLE, ALLURE_ALIASES, type ZoneRow } from './allureTable.ts';

/* =================================================================
   RESOLUTION DE LA ZONE D'ALLURE
   ================================================================= */

export function resoudreCodeCanonique(code: string): string {
  const upper = code.toString().trim().toUpperCase();
  for (const cle of Object.keys(ALLURE_ALIASES)) {
    if (ALLURE_ALIASES[cle].indexOf(upper) !== -1) return cle;
  }
  return upper;
}

export function trouverZone(table: ZoneRow[], code: string): ZoneRow | null {
  const canonique = resoudreCodeCanonique(code);
  for (const row of table) {
    if (row.code.toString().trim().toUpperCase() === canonique) return row;
  }
  return null;
}

/* =================================================================
   NORMALISATION DES GUILLEMETS TYPOGRAPHIQUES
   ================================================================= */

export function normaliserGuillemets(str: string): string {
  return str
    .toString()
    .trim()
    .replace(/[‘’‛′]/g, "'")
    .replace(/[“”„‟″]/g, '"');
}

/* =================================================================
   PARSER DE L'EFFORT
   ================================================================= */

export type ValeurEffort =
  | { val: number; unite: 'm' | 'km' }
  | { val: number; unite: 'temps'; secondes: number };

/**
 * Reconnait : 8' / 8min / 8 min / 1000m / 1km / 10k / 5,5k / 45" / 45s / 45'' / 2'30'' / 2'30"
 * ("k", minuscule ou majuscule, vaut "km" : le texte analyse ici est
 * toujours celui AVANT le "@", ou "10K" ne peut pas etre un code d'allure)
 *
 * "''" (deux apostrophes) est accepte comme unite de secondes seules
 * (ex: 50'') en plus de " et s : c'est la meme convention que celle deja
 * utilisee pour la partie secondes de "2'30''" juste au-dessus, et pour
 * la recuperation (cf parseRecuperation), donc un effort de secondes
 * pures s'ecrit de la meme facon qu'une recuperation de secondes pures.
 */
export function parseValeurEffort(str: string | null | undefined): ValeurEffort | null {
  if (str === null || str === undefined) return null;
  const texte = normaliserGuillemets(str);
  if (!texte) return null;

  let m = texte.match(/^([\d.,]+)\s*(km|k|m)$/i);
  if (m) {
    const distance = parseFloat(m[1].replace(',', '.'));
    if (isNaN(distance) || distance <= 0) return null;
    return { val: distance, unite: m[2].toLowerCase() === 'm' ? 'm' : 'km' };
  }

  // Minutes + secondes, ex: 2'30'' / 2'30"
  m = texte.match(/^([\d.,]+)\s*'\s*([\d.,]+)\s*(?:''|"|s)?$/i);
  if (m) {
    const minutesEffort = parseFloat(m[1].replace(',', '.'));
    const secondesEffort = parseFloat(m[2].replace(',', '.'));
    if (isNaN(minutesEffort) || isNaN(secondesEffort) || minutesEffort < 0 || secondesEffort < 0 || secondesEffort >= 60) return null;
    const total = minutesEffort * 60 + secondesEffort;
    if (total <= 0) return null;
    return { val: total, unite: 'temps', secondes: total };
  }

  m = texte.match(/^([\d.,]+)\s*(min|''|'|"|s)$/i);
  if (m) {
    const valeur = parseFloat(m[1].replace(',', '.'));
    if (isNaN(valeur) || valeur <= 0) return null;
    const unite = m[2].toLowerCase();
    const enMinutes = unite === "'" || unite === 'min';
    return { val: valeur, unite: 'temps', secondes: enMinutes ? valeur * 60 : valeur };
  }

  return null;
}

/* =================================================================
   PARSER DE LA RECUPERATION
   ================================================================= */

export type Recuperation = { secondes: number; affichage: string; distanceM?: number };

/**
 * Accepte (le "R" et le separateur "-"/"/" sont optionnels) :
 *   R3'  3'  R0,8'  0,8'  R1,5'  1,5'
 *   R1'20''  1'20"  R90"  90s  R2min  R2
 *   R1km  1km  R400m  400m
 * Un nombre sans unite est interprete comme des MINUTES.
 *
 * Une recuperation exprimee en distance ("Rkm"/"Rm", ex: "R1km", "R400m")
 * est convertie en temps equivalent a l'allure EASY de la table (meme
 * convention que calculerDistanceRecuperation, qui estime a l'inverse la
 * distance parcourue en recuperation a partir d'un temps et de l'allure
 * EASY - l'aller-retour distance <-> temps reste ainsi coherent). Le champ
 * distanceM du resultat conserve la distance telle que saisie.
 */
export function parseRecuperation(str: string | null | undefined, vma: number, table: ZoneRow[]): Recuperation | null {
  if (str === null || str === undefined) return null;
  let texte = normaliserGuillemets(str);
  if (!texte) return null;

  texte = texte.replace(/^[-/]\s*/, '').replace(/^R\s*/i, '').trim();
  if (!texte) return null;

  let m = texte.match(/^([\d.,]+)\s*(km|m)$/i);
  if (m) {
    const valeur = parseFloat(m[1].replace(',', '.'));
    if (isNaN(valeur) || valeur <= 0) return null;
    const uniteDistance = m[2].toLowerCase();
    const distanceM = uniteDistance === 'km' ? valeur * 1000 : valeur;
    const zoneEasy = trouverZone(table, 'EASY');
    if (!zoneEasy) return null;
    const pctVMAEasy = Number(zoneEasy.pctVMA);
    if (isNaN(pctVMAEasy) || pctVMAEasy <= 0) return null;
    const vitesseEasyMs = (vma * pctVMAEasy * 1000) / 3600;
    if (!vitesseEasyMs || vitesseEasyMs <= 0) return null;
    return { secondes: distanceM / vitesseEasyMs, affichage: `${valeur}${uniteDistance}`, distanceM };
  }

  m = texte.match(/^([\d.,]+)\s*'\s*([\d.,]+)\s*(?:''|"|s)?$/i);
  if (m) {
    const minutes = parseFloat(m[1].replace(',', '.'));
    const secondes = parseFloat(m[2].replace(',', '.'));
    if (isNaN(minutes) || isNaN(secondes) || minutes < 0 || secondes < 0 || secondes >= 60) return null;
    return { secondes: minutes * 60 + secondes, affichage: `${minutes}'${secondes}''` };
  }

  m = texte.match(/^([\d.,]+)\s*(?:''|"|s)$/i);
  if (m) {
    const secondesDec = parseFloat(m[1].replace(',', '.'));
    if (isNaN(secondesDec) || secondesDec < 0) return null;
    return { secondes: secondesDec, affichage: `${secondesDec}s` };
  }

  m = texte.match(/^([\d.,]+)\s*(?:'|min)$/i);
  if (m) {
    const minutesDec = parseFloat(m[1].replace(',', '.'));
    if (isNaN(minutesDec) || minutesDec < 0) return null;
    return { secondes: minutesDec * 60, affichage: `${minutesDec}'` };
  }

  m = texte.match(/^([\d.,]+)$/);
  if (m) {
    const minutesSansUnite = parseFloat(m[1].replace(',', '.'));
    if (isNaN(minutesSansUnite) || minutesSansUnite < 0) return null;
    return { secondes: minutesSansUnite * 60, affichage: `${minutesSansUnite}'` };
  }

  return null;
}

/* =================================================================
   ALLURE PROGRESSIVE ("30'@HP-10K")
   ================================================================= */

/**
 * Code d'allure de la table (ou alias), qui ne se lit pas comme une
 * recuperation : "10K", "MP" oui ; "R3", "3", "400m" non (recuperations).
 */
export function estCodeAllure(texte: string): boolean {
  const t = normaliserGuillemets(texte);
  if (!/^[A-Za-zÀ-ÿ0-9]+$/.test(t)) return false;
  if (parseRecuperation(t, 16, ALLURE_TABLE)) return false;
  const canonique = resoudreCodeCanonique(t);
  return ALLURE_TABLE.some((z) => z.code === canonique);
}

export type AllureLue = { code: string; codeFin: string | null; reste: string };

/**
 * Lit l'allure apres "@" : un code ("MP"), ou deux codes separes par "-"
 * pour un effort progressif ("HP-10K"). Le "-" qui suit le premier code
 * annonce sinon une recuperation ("MP-R3'") : le second code n'est retenu
 * que s'il est un code d'allure et pas une recuperation (cf estCodeAllure).
 * `reste` : le texte qui suit l'allure (recuperation eventuelle).
 */
export function lireAllure(apresArobase: string): AllureLue | null {
  const mCode = apresArobase.match(/^([A-Za-zÀ-ÿ0-9]+)/);
  if (!mCode) return null;
  let reste = apresArobase.substring(mCode[0].length).trim();
  let codeFin: string | null = null;
  const mFin = reste.match(/^-\s*([A-Za-zÀ-ÿ0-9]+)(?=$|[\s\-/[])/);
  if (mFin && estCodeAllure(mFin[1])) {
    codeFin = mFin[1].toUpperCase();
    reste = reste.substring(mFin[0].length).trim();
  }
  return { code: mCode[1].toUpperCase(), codeFin, reste };
}

/**
 * Notation dont chaque allure apres "@" est ecrite sous son code canonique
 * ("@10k" -> "@10K", "@semi-10k" -> "@HP-10K"), pour l'affichage. Les
 * codes inconnus et les recuperations ("@MP-R3'") sont laisses tels quels.
 * Les conditions connues sont ecrites sous leur libelle ("[cote]" -> "[côte]").
 */
export function normaliserAllures(notation: string): string {
  const canonique = (code: string) => (estCodeAllure(code) ? resoudreCodeCanonique(code) : code);
  return notation
    .replace(/\[([^\]]*)\]/g, (tout, texte) => {
      const condition = trouverCondition(texte);
      return condition ? `[${condition.libelle.toLowerCase()}]` : tout;
    })
    .replace(/@(\s*)([A-Za-zÀ-ÿ0-9]+)(?:\s*-\s*([A-Za-zÀ-ÿ0-9]+)(?=$|[\s\-/+)[]))?/g, (tout, espace, code, fin) => {
    if (fin && !estCodeAllure(fin)) return tout.replace(code, canonique(code));
    return `@${espace}${canonique(code)}${fin ? `-${canonique(fin)}` : ''}`;
  });
}

/* =================================================================
   CONDITIONS D'EXECUTION ("1'@5k[côte]")
   ================================================================= */

export type CodeCondition = 'COTE' | 'DESCENTE' | 'PISTE' | 'TRAIL' | 'TAPIS';

/**
 * Condition d'execution d'un effort. `sansAllure` : l'allure n'y est
 * qu'un repere d'intensite (en cote, l'allure 5 km ne se tient pas) - pas
 * de cible d'allure vers intervals.icu, distance estimee approximative.
 */
export type Condition = { code: CodeCondition; libelle: string; alias: string[]; sansAllure: boolean };

export const CONDITIONS: Condition[] = [
  { code: 'COTE', libelle: 'Côte', alias: ['COTE', 'MONTEE'], sansAllure: true },
  { code: 'DESCENTE', libelle: 'Descente', alias: ['DESCENTE'], sansAllure: true },
  { code: 'TRAIL', libelle: 'Trail', alias: ['TRAIL', 'CHEMIN', 'SENTIER'], sansAllure: true },
  { code: 'PISTE', libelle: 'Piste', alias: ['PISTE', 'STADE'], sansAllure: false },
  { code: 'TAPIS', libelle: 'Tapis', alias: ['TAPIS'], sansAllure: false },
];

/** Etiquette entre crochets ("[côte]"). */
export const RE_CONDITION = /\s*\[([^\]]*)\]/g;

// Ce qui precede une etiquette bien placee : "@CODE" ou "@CODE-CODE".
const RE_AVANT_CONDITION = /@\s*([A-Za-zÀ-ÿ0-9]+)(?:\s*-\s*([A-Za-zÀ-ÿ0-9]+))?$/;

/** Condition d'une etiquette, sans tenir compte de la casse ni des accents ("cote", "Côte"). */
export function trouverCondition(texte: string): Condition | null {
  const t = texte.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
  return CONDITIONS.find((c) => c.alias.includes(t)) ?? null;
}

export type ConditionsLues = { notation: string; conditions: Condition[]; erreur?: string };

/**
 * Lit les etiquettes de condition d'une notation : renvoie la notation
 * sans etiquettes (pour les calculs) et les conditions trouvees (sans
 * doublon). Erreur si une etiquette est inconnue ou n'est pas juste apres
 * une allure.
 */
export function lireConditions(notation: string): ConditionsLues {
  const conditions: Condition[] = [];
  let erreur: string | undefined;
  for (const m of Array.from(notation.matchAll(RE_CONDITION))) {
    const condition = trouverCondition(m[1]);
    if (!condition) {
      if (!erreur) erreur = `Condition inconnue : [${m[1].trim()}] (utilise ${CONDITIONS.map((c) => c.libelle.toLowerCase()).join(', ')})`;
      continue;
    }
    const avant = RE_AVANT_CONDITION.exec(notation.substring(0, m.index));
    if (!avant || (avant[2] && !estCodeAllure(avant[2]))) {
      if (!erreur) erreur = `Condition [${m[1].trim()}] mal placée : écris-la juste après l'allure, ex 1'@5k[côte]`;
      continue;
    }
    if (!conditions.includes(condition)) conditions.push(condition);
  }
  return { notation: notation.replace(RE_CONDITION, ''), conditions, erreur };
}

/** Conditions d'une notation (etiquettes inconnues ignorees), pour l'affichage. */
export function conditionsDe(notation: string | null | undefined): Condition[] {
  if (!notation) return [];
  const conditions: Condition[] = [];
  for (const m of Array.from(notation.matchAll(RE_CONDITION))) {
    const condition = trouverCondition(m[1]);
    if (condition && !conditions.includes(condition)) conditions.push(condition);
  }
  return conditions;
}

/**
 * Segments d'une sequence decoupee sur "-" : un code d'allure qui suit un
 * effort "...@CODE" est le second code d'un effort progressif, recolle a
 * l'effort ("30'@HP", "10K" -> "30'@HP-10K") au lieu d'etre pris pour une
 * recuperation.
 */
export function recollerAlluresProgressives(segments: string[]): string[] {
  const res: string[] = [];
  segments.forEach((segment) => {
    const precedent = res.length > 0 ? res[res.length - 1] : '';
    if (/@\s*[A-Za-zÀ-ÿ0-9]+$/.test(precedent) && estCodeAllure(segment.replace(RE_CONDITION, ''))) {
      res[res.length - 1] = `${precedent}-${segment.trim()}`;
    } else {
      res.push(segment);
    }
  });
  return res;
}

/** Vitesse (m/s) d'un effort a la moyenne des allures (s/km) de deux zones. */
export function vitesseMoyenneMs(vma: number, pctDebut: number, pctFin: number): number {
  const allureMoyenneS = (3600 / (vma * pctDebut) + 3600 / (vma * pctFin)) / 2;
  return 1000 / allureMoyenneS;
}

/* =================================================================
   RPE - COMPOSANTES DU SCORE
   ================================================================= */

function calculerScoreIntensite(pctVMA: number): number {
  if (pctVMA < 0.70) return 2;
  if (pctVMA < 0.75) return 3;
  if (pctVMA < 0.80) return 4;
  if (pctVMA < 0.85) return 5;
  if (pctVMA < 0.90) return 6;
  if (pctVMA < 0.95) return 7;
  if (pctVMA < 1.00) return 8;
  if (pctVMA < 1.05) return 9;
  return 10;
}

function calculerScoreDuree(effortMin: number): number {
  if (effortMin <= 1) return 0;
  if (effortMin <= 3) return 0.25;
  if (effortMin <= 5) return 0.50;
  if (effortMin <= 10) return 0.75;
  if (effortMin <= 15) return 1.00;
  return 1.25;
}

function calculerScoreReps(reps: number): number {
  if (reps <= 3) return 0;
  if (reps <= 5) return 0.25;
  if (reps <= 7) return 0.50;
  if (reps <= 10) return 0.75;
  return 1.00;
}

function calculerScoreRecuperation(ratioRecup: number): number {
  if (ratioRecup >= 1.50) return -0.75;
  if (ratioRecup >= 1.00) return -0.50;
  if (ratioRecup >= 0.75) return -0.25;
  if (ratioRecup >= 0.50) return 0;
  if (ratioRecup >= 0.30) return 0.50;
  return 1.00;
}

/** Score RPE brut (NON arrondi, NON plafonne) d'un bloc homogene. */
function scoreRPEBrut(pctVMA: number, effortS: number, reps: number, recupS: number): number {
  const ratioRecup = effortS > 0 ? recupS / effortS : 0;
  return (
    calculerScoreIntensite(pctVMA) +
    calculerScoreDuree(effortS / 60) +
    calculerScoreReps(reps) +
    calculerScoreRecuperation(ratioRecup)
  );
}

/** Arrondit et plafonne un score brut sur l'echelle 1-10. */
function arrondirRPE(scoreBrut: number): number {
  return Math.max(1, Math.min(10, Math.round(scoreBrut)));
}

/* =================================================================
   DISTANCE PARCOURUE EN RECUPERATION (allure EASY)
   ================================================================= */

function calculerDistanceRecuperation(recupS: number, nbRecuperations: number, vma: number, table: ZoneRow[]): number {
  if (!recupS || !nbRecuperations || nbRecuperations <= 0) return 0;
  const zoneEasy = trouverZone(table, 'EASY');
  if (!zoneEasy) return 0;
  const pctVMAEasy = Number(zoneEasy.pctVMA);
  if (isNaN(pctVMAEasy) || pctVMAEasy <= 0) return 0;
  const vitesseEasyMs = (vma * pctVMAEasy * 1000) / 3600;
  return recupS * nbRecuperations * vitesseEasyMs;
}

/* =================================================================
   ANALYSE D'UN BLOC
   ================================================================= */

export type BlocAnalyse = {
  notation: string;
  nomZone: string;
  code: string;
  nbReps: number;
  effortDistM: number;
  effortTimeS: number;
  recupVal: Recuperation | null;
  nombreRecuperations: number;
  /**
   * Recuperation (secondes) jouee apres la derniere repetition de la ligne
   * quand un autre bloc "+" suit (cf ajouterRecupTransition) ; non comptee
   * dans dureeTotaleS tant que la ligne termine la seance.
   */
  recupFinaleS: number;
  distanceEffortM: number;
  distanceRecuperationM: number;
  distanceTotaleM: number;
  dureeTotaleS: number;
  scoreBrut: number;
  pctVMA: number;
  ratioMin: number;
  ratioMax: number;
  repsMin: number;
  repsMax: number;
  volMin: number;
  volMax: number;
  distMin: number;
  distMax: number;
};

export type BlocErreur = { erreur: string };
export type BlocResultat = BlocAnalyse | BlocErreur | null;

function estErreur(bloc: BlocResultat): bloc is BlocErreur {
  return !!bloc && 'erreur' in bloc;
}

/**
 * Parse et calcule toutes les donnees d'un bloc de seance (ex: "4x(8'@MP-R3')").
 *
 * @param nombreRecuperationsOverride Nombre de recuperations a utiliser a
 *   la place du nbReps-1 par defaut (utilise uniquement pour un bloc
 *   COMPOSE, voir analyserBlocsComposition).
 */
function analyserBlocPourRPE(
  notation: string | null | undefined,
  vma: number,
  table: ZoneRow[],
  nombreRecuperationsOverride?: number,
): BlocResultat {
  if (notation === null || notation === undefined || notation.toString().trim() === '') return null;

  const txt = notation.toString().trim();

  // Le "Nx" est optionnel : une notation sans nombre de repetitions
  // (ex: "3'@RECUP") est interpretee comme une seule repetition. Quand
  // "Nx" est present, le contenu repete doit etre entre parentheses
  // explicites ("6x(1000m@10K-R2')") : la forme sans parentheses
  // ("6x1000m@10K-R2'") etait auparavant tolere silencieusement comme
  // equivalente, mais c'est une source d'ambiguite/d'erreurs de saisie
  // non detectees - elle est desormais rejetee comme une erreur.
  const mReps = txt.match(/(\d+)\s*[xX×]/);
  let nbReps: number;
  let reste: string;

  if (mReps) {
    nbReps = parseInt(mReps[1], 10);
    if (nbReps <= 0) return { erreur: 'Nombre de répétitions invalide' };
    const resteBrut = txt.substring((mReps.index ?? 0) + mReps[0].length).trim();
    if (!/^\(.*\)$/.test(resteBrut)) {
      const prefixeAffiche = mReps[0].replace(/\s+/g, '');
      return {
        erreur: `Format non reconnu : "${prefixeAffiche}" doit être suivi de parenthèses, ex "${prefixeAffiche}(${resteBrut || '...'})"`,
      };
    }
    reste = resteBrut.replace(/[()]/g, '').trim();
  } else {
    nbReps = 1;
    reste = txt.replace(/[()]/g, '').trim();
  }

  const positionArobase = reste.indexOf('@');
  if (positionArobase === -1) return { erreur: 'Format non reconnu : allure introuvable (utilise @)' };

  const effortPart = reste.substring(0, positionArobase).trim();
  const apresArobase = reste.substring(positionArobase + 1).trim();

  const allureLue = lireAllure(apresArobase);
  if (!allureLue) return { erreur: "Code d'allure introuvable après @" };

  const { code, codeFin } = allureLue;
  const apresCode = allureLue.reste;

  const recupVal = parseRecuperation(apresCode, vma, table);
  // Texte apres l'allure qui n'est pas une recuperation valide (ex: "R'1'15''") :
  // erreur, plutot qu'une recuperation ignoree silencieusement.
  if (apresCode !== '' && !recupVal) return { erreur: `Récupération non reconnue : "${apresCode.replace(/^[-/]\s*/, '')}" (ex: R1'30'', R90", R400m)` };
  const effortVal = parseValeurEffort(effortPart);
  if (!effortVal) return { erreur: "Distance ou temps d'effort introuvable (ex: 8', 1000m ou 10k)" };

  const zone = trouverZone(table, code);
  if (!zone) return { erreur: `Code d'allure non reconnu : ${code} (utilise VMA, 3K, 5K, 10K, SEMI/HP ou MP)` };

  const pctDebut = Number(zone.pctVMA);
  if (isNaN(pctDebut) || pctDebut <= 0) return { erreur: `Pourcentage VMA invalide pour ${zone.nom}` };

  // Effort progressif : distance et charge a la moyenne des deux allures ;
  // fourchettes de controle (reps, volume, recup) de la zone de depart.
  let nomZone = zone.nom;
  let vitesseMs = (vma * pctDebut * 1000) / 3600;
  if (codeFin) {
    const zoneFin = trouverZone(table, codeFin);
    if (!zoneFin) return { erreur: `Code d'allure non reconnu : ${codeFin} (utilise VMA, 3K, 5K, 10K, SEMI/HP ou MP)` };
    const pctFin = Number(zoneFin.pctVMA);
    if (isNaN(pctFin) || pctFin <= 0) return { erreur: `Pourcentage VMA invalide pour ${zoneFin.nom}` };
    nomZone = `${zone.nom} → ${zoneFin.nom}`;
    vitesseMs = vitesseMoyenneMs(vma, pctDebut, pctFin);
  }
  const pctVMA = (vitesseMs * 3.6) / vma;

  let effortDistM: number;
  let effortTimeS: number;
  if (effortVal.unite === 'm') {
    effortDistM = effortVal.val;
    effortTimeS = effortDistM / vitesseMs;
  } else if (effortVal.unite === 'km') {
    effortDistM = effortVal.val * 1000;
    effortTimeS = effortDistM / vitesseMs;
  } else {
    effortTimeS = effortVal.secondes;
    effortDistM = effortTimeS * vitesseMs;
  }

  // Recuperation consideree comme nulle si absente (blocs "continus").
  const recupSecondes = recupVal ? recupVal.secondes : 0;

  // Pas de recuperation apres la derniere repetition (sauf override
  // explicite, voir analyserBlocsComposition pour un bloc COMPOSE).
  const nombreRecuperations =
    typeof nombreRecuperationsOverride === 'number' ? nombreRecuperationsOverride : Math.max(0, nbReps - 1);

  const distanceEffortM = nbReps * effortDistM;
  const distanceRecuperationM = calculerDistanceRecuperation(recupSecondes, nombreRecuperations, vma, table);
  const distanceTotaleM = distanceEffortM + distanceRecuperationM;
  const dureeTotaleS = nbReps * effortTimeS + nombreRecuperations * recupSecondes;
  const scoreBrut = scoreRPEBrut(pctVMA, effortTimeS, nbReps, recupSecondes);

  return {
    notation: txt,
    nomZone,
    code,
    nbReps,
    effortDistM,
    effortTimeS,
    recupVal,
    nombreRecuperations,
    recupFinaleS: recupSecondes,
    distanceEffortM,
    distanceRecuperationM,
    distanceTotaleM,
    dureeTotaleS,
    scoreBrut,
    pctVMA,
    ratioMin: zone.ratioMin,
    ratioMax: zone.ratioMax,
    repsMin: zone.repsMin,
    repsMax: zone.repsMax,
    volMin: zone.volMin,
    volMax: zone.volMax,
    distMin: zone.distMin,
    distMax: zone.distMax,
  };
}

/* =================================================================
   DECOMPOSITION DES COMPOSITIONS ("+"), BLOCS COMPOSES ET IMBRIQUES
   ================================================================= */

/**
 * A l'interieur de parentheses, "+" enchaine les efforts d'une meme
 * repetition comme "-" : "2x(3000m@MP-R2'+800m@5k)" = "2x(3000m@MP-R2'-800m@5k)".
 * Seuls les "+" hors parentheses separent des blocs.
 */
export function remplacerPlusInternes(notation: string): string {
  let profondeur = 0;
  let resultat = '';
  for (const c of notation) {
    if (c === '(') profondeur += 1;
    if (c === ')') profondeur = Math.max(0, profondeur - 1);
    resultat += c === '+' && profondeur > 0 ? '-' : c;
  }
  return resultat;
}

function decomposerEnBlocs(notation: string | null | undefined): string[] {
  if (notation === null || notation === undefined) return [];
  return remplacerPlusInternes(notation.toString())
    .split('+')
    .map((p) => p.trim())
    .filter((p) => p !== '');
}

/** Decoupe sur un separateur sans jamais couper a l'interieur de parentheses. */
export function diviserEnRespectantParentheses(texte: string, separateur: string): string[] {
  const segments: string[] = [];
  let profondeur = 0;
  let courant = '';
  for (let i = 0; i < texte.length; i += 1) {
    const c = texte.charAt(i);
    if (c === '(') profondeur += 1;
    if (c === ')') profondeur -= 1;
    if (c === separateur && profondeur === 0) {
      segments.push(courant);
      courant = '';
    } else {
      courant += c;
    }
  }
  segments.push(courant);
  return segments;
}

function estUniteImbriquee(segment: string): boolean {
  return /^\d+\s*[xX×]\s*\(.*\)$/.test(segment);
}
function estUnite(segment: string): boolean {
  return estUniteImbriquee(segment) || segment.indexOf('@') !== -1;
}

type PaireEffortRecup = { effort: string; recup: string | null };

function decomposerSequenceComposee(contenu: string): PaireEffortRecup[] {
  const segments = recollerAlluresProgressives(
    contenu
      .split('-')
      .map((s) => s.trim())
      .filter((s) => s !== ''),
  );
  const paires: PaireEffortRecup[] = [];
  let i = 0;
  while (i < segments.length) {
    const segment = segments[i];
    if (segment.indexOf('@') === -1) {
      i += 1;
      continue;
    }
    const effort = segment;
    let recup: string | null = null;
    if (i + 1 < segments.length && segments[i + 1].indexOf('@') === -1) {
      recup = segments[i + 1];
      i += 2;
    } else {
      i += 1;
    }
    paires.push({ effort, recup });
  }
  return paires;
}

type UniteImbriquee = { texte: string; imbriquee: boolean; recup: string | null };

function decomposerSequenceComposeeImbriquee(segments: string[]): UniteImbriquee[] {
  const unites: UniteImbriquee[] = [];
  let i = 0;
  while (i < segments.length) {
    const segment = segments[i];
    if (!estUnite(segment)) {
      i += 1;
      continue;
    }
    let recup: string | null = null;
    if (i + 1 < segments.length && !estUnite(segments[i + 1])) {
      recup = segments[i + 1];
      i += 2;
    } else {
      i += 1;
    }
    unites.push({ texte: segment, imbriquee: estUniteImbriquee(segment), recup });
  }
  return unites;
}

/**
 * Un bloc issu de decomposerEnBlocs peut lui-meme etre un bloc COMPOSE ou
 * IMBRIQUE. Renvoie toujours un tableau de blocs analyses (un seul element
 * pour un bloc simple).
 */
function analyserBlocsComposition(texteBloc: string | null | undefined, vma: number, table: ZoneRow[]): BlocResultat[] {
  if (texteBloc === null || texteBloc === undefined || texteBloc.toString().trim() === '') {
    return [null];
  }

  const txt = texteBloc.toString().trim();

  // "Nx Mx (...)" SANS parentheses autour de "Mx(...)" (ex:
  // "2x5x(1'15''@5k-R1'15'')") - la notation a 2 niveaux d'imbrication
  // telle que specifiee a l'origine, a distinguer de "Nx(Mx(...)-Rrecup)"
  // AVEC parentheses externes (analyserBlocComposeImbrique ci-dessous),
  // qui permet une recup distincte entre "series". Sans parentheses
  // externes, il n'y a pas de recup separee entre series : on aplatit
  // simplement en N*M repetitions du bloc interieur, avec la meme recup
  // partout (comme si on avait saisi "(N*M)x(...)" directement).
  const mImbriqueSansParentheses = txt.match(/^(\d+)\s*[xX×]\s*(\d+)\s*[xX×]\s*\((.*)\)$/);
  if (mImbriqueSansParentheses) {
    const n = parseInt(mImbriqueSansParentheses[1], 10);
    const m = parseInt(mImbriqueSansParentheses[2], 10);
    const contenu = mImbriqueSansParentheses[3];
    if (n > 0 && m > 0) {
      return analyserBlocsComposition(`${n * m}x(${contenu})`, vma, table);
    }
  }

  // Suite de plusieurs efforts SANS "Nx" (ex: "(10km@MP-1km@RECUP)" ou
  // "10km@MP-1km@RECUP") : une seule repetition de la sequence, analysee
  // comme "1x(...)" (sinon le second effort serait pris pour une recup).
  if (!/^\d+\s*[xX×]/.test(txt) && (txt.match(/@/g) || []).length >= 2) {
    const englobe = /^\((.*)\)$/.exec(txt);
    const interieur = englobe && (englobe[1].match(/\(/g) || []).length === (englobe[1].match(/\)/g) || []).length
      ? englobe[1]
      : txt;
    return analyserBlocsComposition(`1x(${interieur})`, vma, table);
  }

  const mOuter = txt.match(/^(\d+)\s*[xX×]\s*\((.*)\)$/);
  if (mOuter) {
    const n = parseInt(mOuter[1], 10);
    const contenu = mOuter[2];

    if (contenu.indexOf('(') !== -1) {
      const resultatsImbriques = analyserBlocComposeImbrique(n, contenu, vma, table);
      if (resultatsImbriques) return resultatsImbriques;
    } else {
      const nbArobases = (contenu.match(/@/g) || []).length;
      if (nbArobases >= 2) {
        const paires = decomposerSequenceComposee(contenu);
        if (paires.length > 0) {
          return paires.map((paire, index) => {
            const estDernierEffortDuCycle = index === paires.length - 1;
            const nbRecupOverride = estDernierEffortDuCycle ? Math.max(0, n - 1) : n;
            const miniNotation = `${n}x(${paire.effort}${paire.recup ? '-' + paire.recup : ''})`;
            return analyserBlocPourRPE(miniNotation, vma, table, nbRecupOverride);
          });
        }
      }
    }
  }

  return [analyserBlocPourRPE(txt, vma, table)];
}

function analyserBlocComposeImbrique(n: number, contenu: string, vma: number, table: ZoneRow[]): BlocResultat[] | null {
  const segments = recollerAlluresProgressives(
    diviserEnRespectantParentheses(contenu, '-')
      .map((s) => s.trim())
      .filter((s) => s !== ''),
  );
  const unites = decomposerSequenceComposeeImbriquee(segments);
  if (unites.length === 0) return null;

  let resultats: BlocResultat[] = [];
  unites.forEach((unite, index) => {
    const estDerniereUnite = index === unites.length - 1;
    const nbRecupOverrideExterne = estDerniereUnite ? Math.max(0, n - 1) : n;

    if (!unite.imbriquee) {
      const miniNotation = `${n}x(${unite.texte}${unite.recup ? '-' + unite.recup : ''})`;
      resultats.push(analyserBlocPourRPE(miniNotation, vma, table, nbRecupOverrideExterne));
      return;
    }

    resultats = resultats.concat(
      analyserUniteImbriquee(unite.texte, n, nbRecupOverrideExterne, unite.recup, vma, table),
    );
  });

  return resultats;
}

function analyserUniteImbriquee(
  texteImbrique: string,
  n: number,
  nbRecupOverrideExterne: number,
  recupExterneTexte: string | null,
  vma: number,
  table: ZoneRow[],
): BlocResultat[] {
  const interieurs = analyserBlocsComposition(texteImbrique, vma, table);
  const recupExterneVal = recupExterneTexte ? parseRecuperation(recupExterneTexte, vma, table) : null;
  if (recupExterneTexte && !recupExterneVal) {
    return [{ erreur: `Récupération non reconnue : "${recupExterneTexte}" (ex: R1'30'', R90", R400m)` }];
  }
  const recupExterneSecondes = recupExterneVal ? recupExterneVal.secondes : 0;

  return interieurs.map((interieur, index) => {
    if (!interieur || estErreur(interieur)) return interieur;

    const estDernierInterieur = index === interieurs.length - 1;
    const recupSecondesPourCetteLigne = estDernierInterieur ? recupExterneSecondes : 0;
    const nbRecupPourCetteLigne = estDernierInterieur ? nbRecupOverrideExterne : 0;

    const nbRepsTotal = n * interieur.nbReps;
    const distanceEffortM = nbRepsTotal * interieur.effortDistM;
    const distanceRecupExterneM = calculerDistanceRecuperation(recupSecondesPourCetteLigne, nbRecupPourCetteLigne, vma, table);
    const distanceTotaleM = n * interieur.distanceTotaleM + distanceRecupExterneM;
    const dureeTotaleS = n * interieur.dureeTotaleS + nbRecupPourCetteLigne * recupSecondesPourCetteLigne;

    const scoreBrut = scoreRPEBrut(
      interieur.pctVMA,
      interieur.effortTimeS,
      nbRepsTotal,
      interieur.recupVal ? interieur.recupVal.secondes : 0,
    );

    const resultat: BlocAnalyse = {
      notation: `${n}x(${texteImbrique}${recupExterneTexte ? '-' + recupExterneTexte : ''})`,
      nomZone: interieur.nomZone,
      code: interieur.code,
      nbReps: nbRepsTotal,
      effortDistM: interieur.effortDistM,
      effortTimeS: interieur.effortTimeS,
      recupVal: interieur.recupVal,
      nombreRecuperations: n * interieur.nombreRecuperations + nbRecupPourCetteLigne,
      recupFinaleS: estDernierInterieur && recupExterneVal ? recupExterneSecondes : interieur.recupFinaleS,
      distanceEffortM,
      distanceRecuperationM: distanceTotaleM - distanceEffortM,
      distanceTotaleM,
      dureeTotaleS,
      scoreBrut,
      pctVMA: interieur.pctVMA,
      ratioMin: interieur.ratioMin,
      ratioMax: interieur.ratioMax,
      repsMin: interieur.repsMin,
      repsMax: interieur.repsMax,
      volMin: interieur.volMin,
      volMax: interieur.volMax,
      distMin: interieur.distMin,
      distMax: interieur.distMax,
    };
    return resultat;
  });
}

/**
 * Recuperation de transition entre deux blocs "+" : la recuperation ecrite
 * dans un bloc est jouee apres sa derniere repetition quand un autre bloc
 * suit (ex: "(1K@HP-R2'30'')+(2K@HP-R2'30'')+1K@HP", ou
 * "2x(4'@10K-R3')+6x(1'@3K-R1')" : 3' avant les 1'), comme sur la montre
 * (cf intervalsIcu.ts). Elle est ajoutee a la derniere ligne du bloc ; la
 * derniere recuperation de la seance, elle, n'est jamais comptee.
 */
function ajouterRecupTransition(resultats: BlocResultat[], vma: number, table: ZoneRow[]): BlocResultat[] {
  const dernier = resultats[resultats.length - 1];
  if (!dernier || estErreur(dernier) || !(dernier.recupFinaleS > 0)) return resultats;
  const distanceM = calculerDistanceRecuperation(dernier.recupFinaleS, 1, vma, table);
  const avecTransition: BlocAnalyse = {
    ...dernier,
    nombreRecuperations: dernier.nombreRecuperations + 1,
    distanceRecuperationM: dernier.distanceRecuperationM + distanceM,
    distanceTotaleM: dernier.distanceTotaleM + distanceM,
    dureeTotaleS: dernier.dureeTotaleS + dernier.recupFinaleS,
  };
  return [...resultats.slice(0, -1), avecTransition];
}

/** Blocs analyses de chaque bloc "+" de la notation, recuperations de transition comprises. */
function analyserBlocs(blocsTexte: string[], vma: number, table: ZoneRow[]): BlocResultat[][] {
  return blocsTexte.map((texteBloc, index) => {
    const resultats = analyserBlocsComposition(texteBloc, vma, table);
    return index < blocsTexte.length - 1 ? ajouterRecupTransition(resultats, vma, table) : resultats;
  });
}

/* =================================================================
   HELPERS DE PRESENTATION
   ================================================================= */

/** Convertit un nombre de secondes en "m:ss". */
export function formatTemps(secondes: number): string {
  const s = Math.round(secondes);
  const minutes = Math.floor(s / 60);
  const reste = s % 60;
  return `${minutes}:${reste < 10 ? '0' : ''}${reste}`;
}

function ratioInRange(ratio: number, ratioMin: number, ratioMax: number): boolean {
  const EPSILON = 0.0001;
  return ratio + EPSILON >= ratioMin && ratio - EPSILON <= ratioMax;
}

function controlerPlage(
  estConfiguree: boolean,
  estDansLaPlage: boolean,
  messageOk: string,
  messageKo: string,
  messageNonConfigure: string,
): string {
  if (!estConfiguree) return `ℹ️ ${messageNonConfigure}`;
  return (estDansLaPlage ? '✅ ' : '⚠️ ') + (estDansLaPlage ? messageOk : messageKo);
}

/* =================================================================
   DETAIL PAR BLOC (equivalent ANALYSE_SEANCE)
   ================================================================= */

export type LigneDetail = {
  erreur?: string;
  nomZone?: string;
  nbReps?: number;
  effortDistM?: number;
  // Valeurs brutes (secondes) en plus des chaines deja formatees
  // ci-dessous, pour permettre un tri numerique correct dans un tableau
  // (ex: DataGrid) plutot qu'un tri alphabetique sur "6:00"/"12:00".
  effortTimeS?: number;
  effortTimeFormatted?: string;
  recuperationS?: number;
  recuperationAffichage?: string;
  dureeTotaleS?: number;
  dureeTotaleFormatted?: string;
  distanceTotaleM?: number;
  ua?: number;
  rpe?: number;
  checkDistance?: string;
  checkReps?: string;
  checkVolume?: string;
  checkRecup?: string;
};

function construireLigneDepuisBloc(bloc: BlocResultat): LigneDetail {
  if (!bloc) {
    return { erreur: "Saisis une séance, ex: 4x(8'@MP-R3')" };
  }
  if (estErreur(bloc)) {
    return { erreur: bloc.erreur };
  }

  const checkDistance = controlerPlage(
    !isNaN(bloc.distMin) && !isNaN(bloc.distMax),
    bloc.effortDistM >= bloc.distMin && bloc.effortDistM <= bloc.distMax,
    `distance adaptée à ${bloc.nomZone}`,
    `distance inhabituelle pour ${bloc.nomZone} (usuel : ${bloc.distMin}-${bloc.distMax} m)`,
    'distance non configurée',
  );

  const checkReps = controlerPlage(
    !isNaN(bloc.repsMin) && !isNaN(bloc.repsMax),
    bloc.nbReps >= bloc.repsMin && bloc.nbReps <= bloc.repsMax,
    'nb reps dans la fourchette',
    `hors fourchette (${bloc.repsMin} à ${bloc.repsMax})`,
    'nombre de reps non configuré',
  );

  const checkVolume = controlerPlage(
    !isNaN(bloc.volMin) && !isNaN(bloc.volMax),
    bloc.distanceEffortM >= bloc.volMin && bloc.distanceEffortM <= bloc.volMax,
    "volume d'effort dans la fourchette",
    `hors fourchette (${bloc.volMin}-${bloc.volMax} m), ici ${Math.round(bloc.distanceEffortM)} m`,
    'volume non configuré',
  );

  let checkRecup = 'ℹ️ récup non analysée';
  const rpe = arrondirRPE(bloc.scoreBrut);
  const ua = Math.round(rpe * (bloc.dureeTotaleS / 60));

  if (bloc.recupVal) {
    const ratio = bloc.recupVal.secondes / bloc.effortTimeS;
    checkRecup = controlerPlage(
      !isNaN(bloc.ratioMin) && !isNaN(bloc.ratioMax),
      ratioInRange(ratio, bloc.ratioMin, bloc.ratioMax),
      `récup cohérente avec ${bloc.nomZone}`,
      `récup hors norme pour ${bloc.nomZone} (ratio conseillé : ${formatTemps(bloc.ratioMin * bloc.effortTimeS)} à ${formatTemps(bloc.ratioMax * bloc.effortTimeS)})`,
      'ratio de récup non configuré',
    );
  }

  return {
    nomZone: bloc.nomZone,
    nbReps: bloc.nbReps,
    effortDistM: Math.round(bloc.effortDistM),
    effortTimeS: bloc.effortTimeS,
    effortTimeFormatted: formatTemps(bloc.effortTimeS),
    recuperationS: bloc.recupVal ? bloc.recupVal.secondes : 0,
    recuperationAffichage: bloc.recupVal ? bloc.recupVal.affichage : '-',
    dureeTotaleS: bloc.dureeTotaleS,
    dureeTotaleFormatted: formatTemps(bloc.dureeTotaleS),
    distanceTotaleM: Math.round(bloc.distanceTotaleM),
    ua,
    rpe,
    checkDistance,
    checkReps,
    checkVolume,
    checkRecup,
  };
}

export function analyserSeanceDetail(notation: string, vma: number, table: ZoneRow[] = ALLURE_TABLE): LigneDetail[] {
  const lues = lireConditions(notation ?? '');
  if (lues.erreur) return [{ erreur: lues.erreur }];
  const blocsTexte = decomposerEnBlocs(lues.notation);
  if (blocsTexte.length === 0) {
    return [{ erreur: "Saisis une séance, ex: 4x(8'@MP-R3')" }];
  }
  return analyserBlocs(blocsTexte, vma, table).flat().map(construireLigneDepuisBloc);
}

/* =================================================================
   SYNTHESE DE SEANCE (equivalent RPE_SEANCE_MULTIBLOCS / UA_RPE_SEANCE)
   ================================================================= */

export type Synthese = {
  erreur?: string;
  dureeTotaleS: number;
  distanceTotaleM: number;
  chargeUA: number;
  rpeSeance: number;
  detailBlocs: string;
};

export function calculerSyntheseSeance(
  notation: string | null | undefined,
  vma: number,
  table: ZoneRow[] = ALLURE_TABLE,
): Synthese {
  const vide: Synthese = { dureeTotaleS: 0, distanceTotaleM: 0, chargeUA: 0, rpeSeance: 0, detailBlocs: '' };

  if (notation === null || notation === undefined || notation.toString().trim() === '') {
    return { ...vide, erreur: "Indique au moins un bloc de séance, ex: 4x(20'@MP-R3')" };
  }
  if (isNaN(vma) || !isFinite(vma) || vma < 8 || vma > 30) {
    return { ...vide, erreur: `VMA invalide (${vma}) : attendu en km/h, entre 8 et 30 (ex: 16.5).` };
  }

  // Les conditions d'execution ("[côte]") ne changent pas les calculs.
  const lues = lireConditions(notation.toString());
  if (lues.erreur) return { ...vide, erreur: lues.erreur };
  const sousBlocsTexte = decomposerEnBlocs(lues.notation);
  const blocs: BlocAnalyse[] = [];
  const erreurs: string[] = [];

  analyserBlocs(sousBlocsTexte, vma, table).forEach((resultats, index) => {
    const sousBloc = sousBlocsTexte[index];
    resultats.forEach((resultat) => {
      if (!resultat) return;
      if (estErreur(resultat)) {
        erreurs.push(`'${sousBloc}' -> ${resultat.erreur}`);
      } else {
        blocs.push(resultat);
      }
    });
  });

  if (erreurs.length > 0) {
    return { ...vide, erreur: `Bloc invalide : ${erreurs.join(' | ')}` };
  }
  if (blocs.length === 0) {
    return { ...vide, erreur: 'Aucun bloc de séance valide trouvé' };
  }

  let dureeTotaleS = 0;
  let distanceTotaleM = 0;
  let sommePonderee = 0;
  let chargeUA = 0; // methode Foster : Sigma(RPE_bloc x duree_bloc en minutes)

  blocs.forEach((b) => {
    dureeTotaleS += b.dureeTotaleS;
    distanceTotaleM += b.distanceTotaleM;
    sommePonderee += b.scoreBrut * b.dureeTotaleS;
    chargeUA += arrondirRPE(b.scoreBrut) * (b.dureeTotaleS / 60);
  });

  const rpeSeance = arrondirRPE(sommePonderee / dureeTotaleS);
  const detailBlocs = blocs
    .map((b) => `${b.nomZone} (RPE ${arrondirRPE(b.scoreBrut)}, ${formatTemps(b.dureeTotaleS)})`)
    .join(' + ');

  return { dureeTotaleS, distanceTotaleM, chargeUA, rpeSeance, detailBlocs };
}

export type ResultatSeance = {
  synthese: Synthese;
  detail: LigneDetail[];
};

// `table` : parametres d'allures de l'utilisateur (cf useAllureTable), ou
// la table par defaut ALLURE_TABLE si non fournie.
export function analyserSeance(notation: string, vma: number, table: ZoneRow[] = ALLURE_TABLE): ResultatSeance {
  return {
    synthese: calculerSyntheseSeance(notation, vma, table),
    detail: analyserSeanceDetail(notation, vma, table),
  };
}
